/**
 * DataManager - Manages timeline data including blocks, task groups, and holidays
 * Handles data loading, saving, and manipulation
 */
class DataManager {
  /**
   * Create a DataManager
   * @param {TimelineCalendar} timeline - The timeline instance
   */
  constructor(timeline) {
    this.timeline = timeline;

    this.taskGroups = [];
    this.blocks = [];
    this.markers = [];
    this.holidays = new Map();

    this.monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    this.dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    this._updateNamesFromTimeline();
  }

  /**
   * Update month and day names from timeline's language system
   * @private
   */
  _updateNamesFromTimeline() {
    if (this.timeline && this.timeline.t) {
      this.monthNames = [
        this.timeline.t('January'),
        this.timeline.t('February'),
        this.timeline.t('March'),
        this.timeline.t('April'),
        this.timeline.t('May'),
        this.timeline.t('June'),
        this.timeline.t('July'),
        this.timeline.t('August'),
        this.timeline.t('September'),
        this.timeline.t('October'),
        this.timeline.t('November'),
        this.timeline.t('December')
      ];

      this.dayNames = [
        this.timeline.t('Sun'),
        this.timeline.t('Mon'),
        this.timeline.t('Tue'),
        this.timeline.t('Wed'),
        this.timeline.t('Thu'),
        this.timeline.t('Fri'),
        this.timeline.t('Sat')
      ];
    }
  }

  /**
   * Add a task group to the timeline
   * @param {string} name - Group name
   * @param {number} taskCount - Number of tasks to create in the group
   */
  addTaskGroup(name, taskCount = 1) {
    const group = {
      id: null,
      tempId: Date.now(),
      name: name,
      tasks: []
    };

    for (let i = 0; i < taskCount; i++) {
      group.tasks.push({
        id: null,
        tempId: Date.now() + i,
        name: `${name} ${i + 1}`,
        displayText: this.timeline.options.defaultTaskText || name.substring(0, 3),
        icon: this.timeline.options.defaultIcon
      });
    }

    this.taskGroups.push(group);
    this.timeline.triggerDataChangeCallback();
  }

  /**
   * Clear all data from the timeline
   */
  clearAllData() {
    this.blocks = [];
    this.timeline.dependencyManager.connections = [];
    this.timeline.dependencyManager.rebuildDependencyMap();
    this.markers = [];
  }

  /**
   * Update block dates and positions
   * @param {Object} block - Block to update
   * @returns {Object} - Updated block
   */
  updateBlockDates(block) {
    const itemWidth = this.timeline.options.itemWidth || 30;
    const timelineWidth = this.timeline.headers ? this.timeline.headers.length * itemWidth : 0;

    // กรณีมีเฉพาะ startDay (ดัชนี) แต่ไม่มีวันที่เริ่มต้น
    if (!block.startDate && block.startDay) {
      block.startDate = this.timeline.workingDaysManager.markerDayToDate(block.startDay);
    }
    // กรณีมีเฉพาะตำแหน่ง left แต่ไม่มีวันที่เริ่มต้น
    else if (!block.startDate && block.left !== undefined) {
      block.startDate = this.timeline.workingDaysManager.positionToDate(block.left, itemWidth);
      if (!block.startDate) {
        // ถ้าไม่พบวันที่ ให้ใช้วันแรกของ timeline
        block.startDate = this.timeline.workingDaysManager.getFirstDate();
      }
    }
    // กรณีไม่มีทั้ง startDate, startDay และ left
    else if (!block.startDate) {
      block.startDate = this.timeline.workingDaysManager.getFirstDate();
    }

    // คำนวณวันที่สิ้นสุดจากวันที่เริ่มต้นและระยะเวลา
    block.endDate = this.timeline.workingDaysManager.calculateEndDate(block.startDate, block.duration);

    // ใช้ฟังก์ชันใหม่แทน
    block.startDay = this.timeline.workingDaysManager.calculateBLockIndex(block.startDate);
    block.left = block.startDay * itemWidth;

    // คำนวณความกว้าง (width) จากระยะเวลา
    const calendarDuration = block.duration;
    block.width = calendarDuration * itemWidth;
    block.calendarDuration = calendarDuration;

    // คำนวณ columnStart และ columnEnd
    const startIndex = this.timeline.workingDaysManager.dateToIndex(block.startDate);
    block.columnStart = startIndex;
    block.columnEnd = startIndex + calendarDuration - 1;

    // คำนวณการแสดงผลในบริเวณที่มองเห็น
    block.visibleLeft = block.left;
    block.visibleWidth = block.width;
    block.isPartiallyVisible = false;
    block.isVisible = true;

    if (block.visibleLeft < 0) {
      block.visibleWidth += block.visibleLeft;
      block.visibleLeft = 0;
      block.isPartiallyVisible = true;
    }

    if (block.visibleLeft + block.visibleWidth > timelineWidth) {
      block.visibleWidth = timelineWidth - block.visibleLeft;
      block.isPartiallyVisible = true;
    }

    if (block.visibleWidth <= 0) {
      block.isVisible = false;
    }

    // กำหนดคุณสมบัติเพิ่มเติม
    block.rowIndex = block.rowIndex !== undefined ? block.rowIndex : 0;

    // กำหนดการปรับขนาดได้
    block.isResizable = true;
    block.isResizableLeft = block.left >= 0;
    block.isResizableRight = (block.left + block.width) <= timelineWidth;

    return block;
  }

  /**
   * Check if block dates overlap with other blocks
   * @param {Object} tempBlock - Block to check
   * @returns {boolean} - True if block overlaps with others
   */
  checkBlockDateOverlap(tempBlock) {
    if (this.blocks.length === 0) return false;

    const newRowIndex = tempBlock.rowIndex;
    let rowBlocks = this.blocks.filter(b =>
      b.rowIndex === newRowIndex &&
      b.id !== tempBlock.id
    );

    if (rowBlocks.length === 0) return false;

    rowBlocks.push(tempBlock);

    const blocks = [...rowBlocks].sort((a, b) => {
      return a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0;
    });
    const tempIndex = blocks.indexOf(tempBlock);
    const leftItem = blocks[tempIndex - 1];
    if (leftItem && leftItem.endDate >= tempBlock.startDate) {
      return true;
    }
    const rightItem = blocks[tempIndex + 1];
    if (rightItem && rightItem.startDate <= tempBlock.endDate) {
      return true;
    }

    return false;
  }

  /**
   * Save timeline data to server
   */
  async saveDataToServer() {
    if (this.timeline.isSaving || !this.timeline.currentProject) return;

    this.timeline.isSaving = true;
    clearTimeout(this.timeline.saveTimeout);

    // ตรวจสอบว่าเปิดใช้งานการอัปเดตรายบล็อกหรือไม่
    const useSingleBlockUpdate = this.timeline.options.useServerSideConnections &&
      !this.timeline.options.syncConnectedBlocks;

    this.timeline.saveTimeout = setTimeout(async () => {
      try {
        // ถ้ามีการเปิดใช้ useServerSideConnections แล้วปิด syncConnectedBlocks
        // และไม่มีข้อมูลอื่นที่ต้องบันทึก ให้ข้ามการบันทึกเพราะบล็อกได้ถูกอัปเดตแล้ว
        if (useSingleBlockUpdate && this.changedBlocks.size === 0 &&
          !this.timeline.dependencyManager.hasNewConnections &&
          !this.timeline.markerManager.hasNewMarkers) {
          this.timeline.isSaving = false;
          return;
        }

        // สร้างข้อมูลสำหรับส่งไปยัง API
        const data = {
          projectId: this.timeline.currentProject.id,
          taskGroups: this.taskGroups,
          // ถ้ามีการอัปเดตรายบล็อกแล้ว ไม่ต้องส่งบล็อกอีก
          blocks: useSingleBlockUpdate ? [] : this.blocks.map(block => ({
            id: block.id,
            tempId: block.id || block.tempId,
            row_index: block.rowIndex,
            start_date: block.startDate,
            duration: block.duration,
            color: block.color,
            display_text: block.displayText,
            icon: block.icon,
            progress: block.progress
          })),
          connections: this.timeline.dependencyManager.getConnectionsForDatabase(),
          markers: this.markers.map(marker => ({
            id: marker.id,
            tempId: marker.id || marker.tempId,
            day: marker.day,
            // date คือสิ่งที่ฐานข้อมูลเก็บจริง ส่งไปด้วยเสมอ ไม่งั้น server
            // ต้องเดาจาก start_date + day วัน ซึ่งผิดเมื่อมีวันหยุดถูกซ่อน
            date: marker.date || this.timeline.workingDaysManager.markerDayToDate(marker.day) || null,
            row_index: marker.rowIndex,
            color: marker.color,
            icon: marker.icon || null,
            name: marker.name,
            description: marker.description
          }))
        };

        // เพิ่มฟิลด์ที่แสดงว่าบล็อกได้ถูกอัปเดตแล้ว
        if (useSingleBlockUpdate) {
          data.blocksUpdatedSeparately = true;
        }

        // เพิ่มฟิลด์ที่แสดงว่า marker และ connection ได้ถูกอัปเดตแล้ว
        if (!this.timeline.markerManager.hasNewMarkers) {
          data.markersUpdatedSeparately = true;
        }
        if (!this.timeline.dependencyManager.hasNewConnections) {
          data.connectionsUpdatedSeparately = true;
        }

        if (typeof this.timeline.options.onSaveData === 'function') {
          this.timeline.options.onSaveData(data);
        } else {
          const result = await this.requestApi('save_timeline', 'POST', data);
          if (result.updatedIds) {
            this.updateIdsFromServer(result.updatedIds);
          }
        }

        // รีเซ็ตสถานะหลังจากบันทึกข้อมูลสำเร็จ
        this.timeline.dependencyManager.resetNewConnectionsFlag();
        this.timeline.markerManager.resetNewMarkersFlag();
      } catch (error) {
        this.timeline.showNotification(this.timeline.t('Error saving data: {message}', {message: error.message}), 'error');
      } finally {
        this.timeline.isSaving = false;
      }
    }, 1000);
  }

  /**
   * Track changed blocks to be saved
   * @type {Set}
   */
  changedBlocks = new Set();

  /**
   * Mark a block as changed
   * @param {Object} block - Block to mark as changed
   */
  markBlockAsChanged(block) {
    this.changedBlocks.add(block.id || block.tempId);
  }

  /**
   * Save only changed blocks to server
   * @returns {Promise<Object>} - Server response
   */
  async saveChangedBlocksToServer() {
    if (this.timeline.isSaving || !this.timeline.currentProject || this.changedBlocks.size === 0) {
      return {success: true, message: 'No changes to save'};
    }

    this.timeline.isSaving = true;
    clearTimeout(this.timeline.saveTimeout);

    try {
      // Get only the changed blocks
      const changedBlocksData = this.blocks
        .filter(block => this.changedBlocks.has(block.id || block.tempId))
        .map(block => ({
          id: block.id,
          tempId: block.id || block.tempId,
          row_index: block.rowIndex,
          start_date: block.startDate,
          duration: block.duration,
          color: block.color,
          display_text: block.displayText,
          icon: block.icon,
          progress: block.progress
        }));

      const data = {
        projectId: this.timeline.currentProject.id,
        blocks: changedBlocksData,
        action: 'update_blocks' // Special action to indicate only blocks are being updated
      };

      if (typeof this.timeline.options.onSaveBlocks === 'function') {
        const result = await this.timeline.options.onSaveBlocks(data);
        this.changedBlocks.clear();
        return result;
      } else {
        const result = await this.requestApi('update_blocks', 'POST', data);

        if (result.updatedIds) {
          this.updateIdsFromServer(result.updatedIds);
        }

        this.timeline.showNotification(this.timeline.t('Block changes saved successfully'), 'success');
        this.changedBlocks.clear();

        // ส่งค่ากลับให้ครบถ้วนเพื่อให้สามารถอัปเดต UI ได้
        return {
          success: true,
          data: result,
          updatedBlocks: result.updatedBlocks || changedBlocksData,
          updatedConnections: result.updatedConnections || []
        };
      }
    } catch (error) {
      this.timeline.showNotification(this.timeline.t('Error saving block changes: {message}', {message: error.message}), 'error');
      return {success: false, error: error.message};
    } finally {
      this.timeline.isSaving = false;
    }
  }

  /**
   * Save only changed connections to server
   * @returns {Promise<Object>} - Server response
   */
  async saveChangedConnectionsToServer() {
    if (this.timeline.isSaving || !this.timeline.currentProject) {
      return {success: true, message: 'No changes to save'};
    }

    const changedConnections = this.timeline.dependencyManager.getChangedConnections();

    // ถ้าไม่มีการเปลี่ยนแปลง connection ใดๆ
    if (changedConnections.updated.length === 0 && changedConnections.deleted.length === 0) {
      return {success: true, message: 'No connections changes to save'};
    }

    this.timeline.isSaving = true;
    clearTimeout(this.timeline.saveTimeout);

    try {
      // เตรียมข้อมูล connection ที่เปลี่ยนแปลงสำหรับส่ง API
      const updatedConnectionsData = changedConnections.updated.map(conn => ({
        id: conn.id,
        tempId: conn.id || conn.tempId,
        fromId: conn.fromId,
        toId: conn.toId,
        gap: conn.gap || 1
      }));

      const data = {
        projectId: this.timeline.currentProject.id,
        connections: updatedConnectionsData,
        deletedConnections: changedConnections.deleted,
        action: 'update_connections' // Action พิเศษสำหรับอัปเดตเฉพาะ connection
      };

      if (typeof this.timeline.options.onSaveConnections === 'function') {
        const result = await this.timeline.options.onSaveConnections(data);
        this.timeline.dependencyManager.clearChangedConnections();
        return result;
      } else {
        const result = await this.requestApi('update_connections', 'POST', data);

        if (result.updatedIds) {
          this.updateIdsFromServer(result.updatedIds);
        }

        this.timeline.showNotification(this.timeline.t('Connection changes saved successfully'), 'success');
        this.timeline.dependencyManager.clearChangedConnections();

        return {
          success: true,
          data: result,
          updatedConnections: result.updatedConnections || updatedConnectionsData
        };
      }
    } catch (error) {
      this.timeline.showNotification(this.timeline.t('Error saving connection changes: {message}', {message: error.message}), 'error');
      return {success: false, error: error.message};
    } finally {
      this.timeline.isSaving = false;
    }
  }

  /**
   * Update IDs from server response
   * @param {Object} updatedIds - Updated IDs from server
   */
  updateIdsFromServer(updatedIds) {
    if (updatedIds.taskGroups) {
      updatedIds.taskGroups.forEach((serverGroup, index) => {
        const matchingGroup = this.taskGroups.find(group => group.id === serverGroup.tempId);
        if (matchingGroup) {
          matchingGroup.id = serverGroup.id;

          if (serverGroup.tasks && matchingGroup.tasks) {
            serverGroup.tasks.forEach((serverTask) => {
              const matchingTask = matchingGroup.tasks.find(task => task.id === serverTask.tempId);
              if (matchingTask) {
                matchingTask.id = serverTask.id;
              }
            });
          }
        }
      });
    }

    if (updatedIds.blocks) {
      updatedIds.blocks.forEach(serverBlock => {
        const matchingBlock = this.blocks.find(b => b.tempId === serverBlock.tempId);
        if (matchingBlock && matchingBlock.id !== serverBlock.id) {
          matchingBlock.id = serverBlock.id;
          matchingBlock.startDate = serverBlock.start_date;
          matchingBlock.duration = serverBlock.duration;

          const blockElement = document.querySelector(`.task-block[data-id="${serverBlock.tempId}"]`);
          if (blockElement) {
            blockElement.dataset.id = serverBlock.id;
          }
        }
      });
    }

    if (updatedIds.connections) {
      updatedIds.connections.forEach(serverConn => {
        const conn = this.timeline.dependencyManager.connections.find(c => c.tempId === serverConn.tempId);
        if (conn) {
          conn.id = serverConn.id;
          conn.fromId = serverConn.from_id;
          conn.toId = serverConn.to_id;
          conn.gap = serverConn.gap;
        }
      });
      this.timeline.dependencyManager.rebuildDependencyMap();
    }

    if (updatedIds.markers) {
      updatedIds.markers.forEach(serverMarker => {
        const marker = this.markers.find(m => m.tempId === serverMarker.tempId);
        if (marker) {
          marker.id = serverMarker.id;
          const markerElement = document.querySelector(`.timeline-marker[data-id="${serverMarker.tempId}"]`);
          if (markerElement) {
            markerElement.dataset.id = serverMarker.id;
          }
        }
      });
    }

    this.timeline.connectionManager.updateConnections();
    this.timeline.markerManager.renderMarkers();
  }

  /**
   * Save timeline settings to server and reload data
   * @param {Object} newSettings - New settings to save
   * @returns {Promise<boolean>} - True if successful
   */
  async saveAndReloadTimeline(newSettings) {
    try {
      // เตรียมข้อมูลสำหรับส่งไปยัง API
      const settings = {
        ...this.timeline.options,
        ...newSettings,
        projectId: this.timeline.currentProject.id,
        startDate: this.timeline.formatDate(this.timeline.options.filterRange.start, 'y-m-d', 0),
        endDate: this.timeline.formatDate(this.timeline.options.filterRange.end, 'y-m-d', 0)
      };

      // ส่งข้อมูลไปยัง API
      const data = await this.requestApi('save_timeline_settings', 'POST', settings);

      if (data && data.status === 'success') {
        // อัพเดทข้อมูลในหน่วยความจำ
        this.setProjectData(data.data);

        // อัพเดทตัวเลือก
        Object.assign(this.timeline.options, newSettings);

        return true;
      } else {
        console.error("API returned error:", data);
        throw new Error(data.message || 'Unknown error');
      }
    } catch (error) {
      console.error("Error in saveAndReloadTimeline:", error);
      this.timeline.showNotification('Error saving settings: ' + error.message, 'error');
      return false;
    }
  }

  /**
   * Load a project by ID
   * @param {number|string} projectId - Project ID to load
   */
  async loadProject(projectId) {
    if (typeof this.timeline.options.onLoadProject === 'function') {
      try {
        const data = await this.timeline.options.onLoadProject(projectId);
        this.setProjectData(data);
      } catch (error) {
        this.timeline.showNotification(this.timeline.t('Error loading project: {message}', {message: error.message}), 'error');
      }
      return;
    }

    try {
      const data = await this.requestApi('load_timeline', 'GET', {project_id: projectId});
      this.setProjectData(DataManager.unwrapApiPayload(data));
    } catch (error) {
      this.timeline.showNotification(this.timeline.t('Error loading project: {message}', {message: error.message}), 'error');
    }
  }

  /**
   * เรียก endpoint ของ timeline แล้วคืน body ที่ server ตอบกลับ
   *
   * ต้องผ่าน ApiService เป็นหลัก เพราะเป็นทางเดียวที่แนบ X-CSRF-Token
   * (และ header ของการยืนยันตัวตน) ให้อัตโนมัติ ซึ่ง endpoint ฝั่ง server ตรวจอยู่
   * ส่วน fetch ตรงๆ เก็บไว้เป็นทางสำรองกรณีใช้ไลบรารีนี้นอก Now.js เท่านั้น
   *
   * @param {string} action - ชื่อ action ต่อท้าย apiBase
   * @param {string} method - GET หรือ POST
   * @param {Object|null} payload - ข้อมูลที่ส่งไป
   * @returns {Promise<Object>} - body ที่ตอบกลับมา
   * @throws {Error} เมื่อ server ตอบว่าไม่สำเร็จ
   */
  async requestApi(action, method = 'GET', payload = null) {
    const url = `${this.timeline.apiBase}${action}`;
    let body;

    if (window.ApiService) {
      const httpResult = method === 'POST'
        ? await ApiService.post(url, payload)
        : await ApiService.get(url, payload || {});
      body = (httpResult && httpResult.data !== undefined) ? httpResult.data : httpResult;
    } else {
      const options = {method: method, headers: {'Content-Type': 'application/json'}};
      let requestUrl = url;
      if (method === 'POST') {
        options.body = JSON.stringify(payload);
      } else if (payload) {
        requestUrl += `?${new URLSearchParams(payload)}`;
      }

      const response = await fetch(requestUrl, options);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      body = await response.json();
    }

    // endpoint กลุ่มนี้ตอบ HTTP 200 เสมอ แล้วบอกผลผ่าน status/success ใน body
    if (body && (body.status === 'error' || body.success === false)) {
      throw new Error(body.message || 'Request failed');
    }

    return body;
  }

  /**
   * แกะ envelope ของ Gcms\Api ออก ( {success, message, code, data, errors} )
   * load_timeline ตอบกลับในรูปแบบนี้ ถ้าส่ง envelope เข้า setProjectData() ตรงๆ
   * ทุกฟิลด์จะเป็น undefined และ timeline จะว่างเปล่า
   * @param {Object} payload - JSON ที่ได้จาก API
   * @returns {Object} - ข้อมูลโครงการจริง
   */
  static unwrapApiPayload(payload) {
    let data = payload;
    // ซ้อนกันได้ 2 ชั้นเมื่อผ่าน HttpClient (response.data) แล้วต่อด้วย envelope (data.data)
    while (data && typeof data === 'object' && data.data !== undefined && data.id === undefined) {
      data = data.data;
    }

    return data;
  }

  /**
   * Set project data from loaded data
   * @param {Object} data - Project data
   */
  setProjectData(data) {
    if (!data) return;

    // ตั้งค่าข้อมูลโปรเจ็ค จาก data
    this.timeline.currentProject = {
      id: data.id,
      name: data.name || 'New Project',
      startDate: data.startDate,
      endDate: data.endDate
    };

    this.timeline.options.filterRange = {
      start: data.startDate,
      end: data.endDate
    };

    // จัดการข้อมูล workingDays ถ้ามี
    if (data.workingDays) {
      this.timeline.workingDaysManager.setWorkingDays(data.workingDays, true);
    }

    // นำเข้าข้อมูลการเชื่อมโยง
    if (data.connections) {
      this.timeline.dependencyManager.importConnectionsFromDatabase(data.connections);
    }

    // นำเข้าข้อมูลบล็อก
    if (data.blocks) {
      this.blocks = Array.isArray(data.blocks) && data.blocks.length > 0 ? data.blocks.map(block => {
        return {
          id: block.id,
          tempId: block.tempId || block.id,
          rowIndex: block.row_index,
          startDate: block.start_date,
          duration: block.duration || 1,
          color: block.color || '#4a6fff',
          displayText: block.display_text || '',
          icon: block.icon || null,
          progress: block.progress || 0
        };
      }) : [];
    }

    // อัปเดตข้อมูลบล็อกแต่ละอัน (คำนวณ startDay, left, width, etc.)
    this.blocks.forEach(block => {
      this.updateBlockDates(block);
    });

    // นำเข้าข้อมูลโครงสร้างต้นไม้ (ถ้ามี)
    if (data.treeNodes) {
      this.timeline.treeData = data.treeNodes;
      this.timeline.processTreeData();
    }

    // นำเข้าข้อมูลมาร์คเกอร์ (ถ้ามี)
    if (data.markers) {
      this.markers = Array.isArray(data.markers) && data.markers.length > 0 ? data.markers.map(marker => {
        // API ส่ง date มาเสมอ แต่ไม่มี day (ดัชนีของ header) เพราะฝั่ง server
        // ไม่รู้ว่า timeline กำลังแสดงวันไหนบ้าง จึงต้องแปลงเองที่นี่
        // เหมือนที่ block ทำผ่าน updateBlockDates()
        const date = marker.date || null;
        let day = marker.day;
        if (day === undefined || day === null) {
          // calculateBLockIndex() เกาะวันที่ใกล้ที่สุดถ้าวันนั้นไม่อยู่ในตารางวันทำงานแล้ว
          day = date ? this.timeline.workingDaysManager.calculateBLockIndex(date) : -1;
        }

        return {
          id: marker.id,
          tempId: marker.tempId || marker.id,
          day: day >= 0 ? day : 0,
          date: date,
          rowIndex: marker.row_index || 0,
          color: marker.color || '#ff4d4d',
          icon: marker.icon || null,
          name: marker.name || marker.text || '',
          description: marker.description || ''
        };
      }) : [];
    }

    // นำเข้าข้อมูลวันหยุด (ถ้ามี)
    if (data.holidays && Array.isArray(data.holidays)) {
      this.holidays.clear();
      data.holidays.forEach(holiday => {
        if (holiday.date) {
          this.holidays.set(holiday.date, {
            name: holiday.name || '',
            type: holiday.type || 'holiday'
          });
        }
      });
    }

    // สร้างข้อมูลตำแหน่งเพิ่มเติมสำหรับบล็อก (เช่น left, width)
    if (this.blocks.length > 0) {
      this.blocks.forEach(block => {
        this.updateBlockDates(block);
      });
    }

    // เพิ่ม event ว่าโหลดข้อมูลเสร็จแล้ว (ถ้าต้องการ)
    if (this.timeline.trigger) {
      this.timeline.trigger('projectDataLoaded', this.timeline.currentProject);
    }
  }

  /**
   * Get all timeline data
   * @returns {Object} - All timeline data
   */
  getAllData() {
    return {
      project: this.timeline.currentProject,
      taskGroups: this.taskGroups,
      blocks: this.blocks.map(block => ({
        id: block.id,
        rowIndex: block.rowIndex,
        startDay: block.startDay,
        duration: block.duration,
        startDate: block.startDate,
        endDate: block.endDate,
        color: block.color,
        displayText: block.displayText,
        icon: block.icon
      })),
      connections: this.timeline.dependencyManager.connections,
      markers: this.markers
    };
  }

  /**
   * Remove a block by ID and send delete command to API directly
   * @param {string|number} blockId - Block ID to remove
   */
  async deleteBlock(blockId) {
    const result = await this.deleteBlockAPI(blockId)
    if (result && result.success) {
      this.timeline.dependencyManager.removeConnectionsByBlockId(blockId);
      // blockId มาจาก input ของ slideout จึงเป็น string เสมอ
      // (String.toInt() ไม่มีใน JS การเรียกเดิมจึงโยน TypeError ทุกครั้งที่ลบจาก panel)
      this.blocks = this.blocks.filter(block => block.id != blockId && block.tempId != blockId);
      this.timeline.domRenderer.renderBlocks();
      this.timeline.connectionManager.updateConnections();

      this.timeline.showNotification('Already deleted Block', 'success');
    } else {
      this.timeline.showNotification('Unable to delete Block: ' + (result.error || 'Unknown error'), 'error');
    }
  }

  /**
   * Deletes a block from the current project via API call
   *
   * @async
   * @param {string|number} blockId - The ID of the block to delete
   * @returns {Promise<Object>} A promise that resolves to the API response with:
   *   @returns {boolean} success - Whether the deletion was successful
   *   @returns {string} [message] - Error message if no active project
   *   @returns {string} [error] - Error message if the API call fails
   * @throws {Error} Will be caught and returned as a failed response object
   */
  async deleteBlockAPI(blockId) {
    if (!this.timeline.currentProject) return {success: false, message: 'No active project'};

    try {
      const data = {
        projectId: this.timeline.currentProject.id,
        blockId: blockId
      };

      return await window.apiHandler.sendRequest('delete_block', 'POST', data);
    } catch (error) {
      return {success: false, error: error.message};
    }
  }

  /**
   * Calculate end date for a block
   * @param {string} startDate - Start date
   * @param {number} duration - Duration in days
   * @returns {string|null} - End date
   */
  calculateEndDate(startDate, duration) {
    return this.timeline.workingDaysManager.calculateEndDate(startDate, duration);
  }

  /**
   * คำนวณวันที่หลังจากเพิ่มจำนวนวันทำงาน (ข้ามวันหยุด)
   * @param {string} startDate - วันที่เริ่มต้น (รูปแบบ YYYY-MM-DD)
   * @param {number} workDays - จำนวนวันทำงานที่ต้องการเพิ่ม
   * @returns {string} - วันที่หลังจากเพิ่มจำนวนวันทำงาน (รูปแบบ YYYY-MM-DD)
   */
  calculateWorkingEndDate(startDate, workDays) {
    return this.timeline.workingDaysManager.addDays(startDate, workDays);
  }

  /**
   * คำนวณวันทำงานระหว่างสองวันที่ (ข้ามวันหยุด)
   * @param {string} startDate - วันที่เริ่มต้น (รูปแบบ YYYY-MM-DD)
   * @param {string} endDate - วันที่สิ้นสุด (รูปแบบ YYYY-MM-DD)
   * @returns {number} - จำนวนวันทำงานระหว่างสองวันที่
   */
  calculateWorkingDaysBetween(startDate, endDate) {
    return this.timeline.workingDaysManager.calculateDuration(startDate, endDate);
  }

  /**
   * Retrieves task information based on a given row index from the timeline's header structure.
   *
   * @param {number} rowIndex - The row index to search for in the task structure
   * @returns {Object|null} An object containing task information if found, or null if not found. The returned object has:
   *   - id {string|null} - The task ID, or null if it's a default task for a subgroup
   *   - name {string} - The task name or generated name for a subgroup
   *   - displayText {string} - The first 3 characters of the name for display purposes
   *   - icon {string} - The task's icon or default icon from timeline options
   *   - groupName {string} - The main group name
   *   - subGroupName {string} - The subgroup name
   */
  getTaskInfoByRowIndex(rowIndex) {
    // คืนค่า task info โดยใช้ structure ที่มีการกำหนด rowIndex
    if (!this.timeline.headerStructure) {
      return null;
    }

    for (const mainGroup of this.timeline.headerStructure.mainGroups) {
      const subGroups = this.timeline.headerStructure.subGroups[mainGroup.id] || [];

      for (const subGroup of subGroups) {
        const tasks = subGroup.tasks || [];

        for (const task of tasks) {
          if (task.rowIndex === rowIndex) {
            return {
              id: task.id,
              name: task.name,
              displayText: task.name.substring(0, 3), // ใช้อักษร 3 ตัวแรกเป็น displayText ถ้าไม่มีการกำหนด
              icon: task.icon || this.timeline.options.defaultIcon,
              groupName: mainGroup.name,
              subGroupName: subGroup.name
            };
          }
        }

        // ถ้าไม่มีงานแต่ rowIndex ตรงกับกลุ่มย่อย
        if (tasks.length === 0 && subGroup.startRowIndex === rowIndex) {
          return {
            id: null,
            name: `${subGroup.name} - Task`,
            displayText: subGroup.name.substring(0, 3),
            icon: this.timeline.options.defaultIcon,
            groupName: mainGroup.name,
            subGroupName: subGroup.name
          };
        }
      }
    }

    return null;
  }

  /**
   * Add a new block to the timeline
   * @param {Object} blockData - Block data
   * @returns {Object} - Created block
   */
  addBlock(blockData) {
    const tempId = Date.now();

    const newBlock = {
      id: 0, // จะได้รับ ID จริงจาก server
      tempId: tempId,
      rowIndex: blockData.rowIndex,
      startDate: blockData.startDate,
      endDate: blockData.endDate,
      duration: blockData.duration || 1,
      color: blockData.color || '#4a6fff',
      displayText: blockData.displayText || '',
      icon: blockData.icon || null,
      progress: blockData.progress || 0,
      isNew: true
    };

    // อัปเดตข้อมูลตำแหน่ง
    this.updateBlockDates(newBlock);

    // เพิ่มเข้าไปในระบบ
    this.blocks.push(newBlock);

    return newBlock;
  }

  /**
   * Update block with server ID
   * @param {string} tempId - Temporary ID
   * @param {number} serverId - Server ID
   * @returns {Object|null} - Updated block
   */
  updateBlockId(tempId, serverId) {
    const block = this.blocks.find(b => b.tempId == tempId);

    if (block) {
      block.id = serverId;
      block.isNew = false;
      return block;
    }

    return null;
  }
}

window.DataManager = DataManager;
